import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, delay, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { Search } from '../../models/search.class';

import { ApiService } from '../../services/api/api.service';
import { UtilsService } from '../../services/utils/utils.service';
import { translate } from '@ngneat/transloco';
import { AlertError, LoadingBulkReportsError } from '../actions';
import * as actions from '../actions/instances.actions';
import {
    currentFilingMonth$,
    currentFilingQuarter$,
    currentFilingYear$,
    getAppConfiguratorsMap$,
    getAppsMetaDataMap$,
    getAssetsWithLatestVersion$,
    getCollaborators$,
    getCurrentOrganizationId$,
    getCustomAppsRestApiName$,
    getCustomInstances$,
    getCustomPeriodicSearchMap$,
    getDownloadableFiles$,
    getInstancesEntityStatus$,
    getInstancesOrgServicesMap$,
    getInstancesState$,
    getInstanceTransformations$,
    getOTPAuthApp$,
    getPreCapturedAttributes$,
    getSearchCriteriaMap$,
    getSelectedAppServiceId$,
    getSubscribedApps$,
    getSubscribedAppsMap$,
} from '../selectors';

@Injectable()
export class InstancesEffects {
    private searchingInstances: {
        [property: string]: {
            [criteriaName: string]: Subject<void>;
        };
    } = {};

    constructor(private actions$: Actions, private _api: ApiService, private store$: Store, private _utils: UtilsService) {}

    getInstance = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstance),
            delay(500),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, servicesMap, metaDataMap, version]) => {
                const app = servicesMap?.[action.serviceId];
                const appMetaData = metaDataMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId];
                const criteria = Search.getRecordsSearchCriteria(
                    app.restApiName,
                    app.serviceId,
                    action.instanceId,
                    { entityId: appMetaData.getPrimaryEntity()?.uid, parentRecordId: undefined },
                    1
                );
                return from(
                    this._api.instances.searchInstances({
                        entityCriterias: criteria as any,
                        loadAssetRequests: true,
                        noAlerts: action.noAlerts,
                        searchAfter: undefined,
                        size: 1,
                    })
                ).pipe(
                    map(res =>
                        actions.SetInstance({
                            instance: res?.instances?.[0] || ({} as any),
                            instanceId: action.instanceId,
                        })
                    ),
                    catchError(() => {
                        return [];
                    })
                );
            })
        )
    );

    searchInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.SearchInstances),
            delay(10),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getInstancesOrgServicesMap$),
                this.store$.select(getSearchCriteriaMap$)
            ),
            mergeMap(([action, organizationId, servicesMap, searchCriteriaMap]) => {
                const userCriteria = CommonUtilsService.cloneObject(
                    searchCriteriaMap?.[action.serviceId]?.instances?.[action.criteriaName]
                );
                const serviceMap = servicesMap?.[action.serviceId];
                const pagination = serviceMap?.pagination;
                if (serviceMap?.loading?.instances && !action.forceFetch) {
                    return [];
                }
                const expectedCount = ((pagination?.pageIndex || 0) + 1) * (pagination?.size || 0);
                const storedCount = serviceMap?.searchedInstances?.[action.criteriaName]?.length;
                if (this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default']) {
                    this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default']?.next();
                    this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default']?.complete();
                    delete this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default'];
                }
                if (storedCount >= expectedCount && !action.forceFetch) {
                    return [];
                }
                if (storedCount > 0 && !pagination?.pagingState && !action.forceFetch) {
                    return [];
                }
                if (pagination?.size === undefined && !action.noPagination) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.SearchInstances(action));
                    }, 100);
                    return [];
                }
                !action.isBackgroundProcess &&
                    this.store$.dispatch(
                        actions.SetInstancesLoading({
                            category: 'instances',
                            loading: true,
                            organizationId,
                            serviceId: action.serviceId,
                        })
                    );
                const searchPromise = this._api.instances.searchInstances({
                    ...userCriteria,
                    noAlerts: action.noAlerts,
                    size: pagination?.size || userCriteria?.size,
                    searchAfter: !action.forceFetch ? pagination?.pagingState : undefined,
                });
                this.searchingInstances[action.serviceId] = this.searchingInstances[action.serviceId] || {};
                this.searchingInstances[action.serviceId][action.criteriaName || 'default'] = new Subject();
                return from(searchPromise).pipe(
                    takeUntil(this.searchingInstances[action.serviceId][action.criteriaName || 'default']),
                    map(res => {
                        this.searchingInstances[action.serviceId][action.criteriaName || 'default']?.next();
                        this.searchingInstances[action.serviceId][action.criteriaName || 'default']?.complete();
                        delete this.searchingInstances[action.serviceId][action.criteriaName || 'default'];
                        !action.isBackgroundProcess &&
                            this.store$.dispatch(
                                actions.SetInstancesLoading({
                                    category: 'instances',
                                    loading: false,
                                    organizationId,
                                    serviceId: action.serviceId,
                                })
                            );
                        this.store$.dispatch(
                            actions.SetInstancesPagingState({
                                organizationId,
                                serviceId: action.serviceId,
                                pagingState: pagination?.size === res?.instances?.length ? res?.searchAfter : undefined,
                            })
                        );
                        this.store$.dispatch(
                            actions.SetSearchInstancesResponse({
                                instances: res.instances,
                                serviceId: action.serviceId,
                                criteriaName: action.criteriaName,
                                organizationId,
                                module: action.module,
                                forceFetch: action.forceFetch,
                            })
                        );
                        return [];
                    }),
                    catchError(() => {
                        !action.isBackgroundProcess &&
                            this.store$.dispatch(
                                actions.SetInstancesLoading({
                                    category: 'instances',
                                    loading: false,
                                    organizationId,
                                    serviceId: action.serviceId,
                                })
                            );
                        this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default']?.next();
                        this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default']?.complete();
                        delete this.searchingInstances[action.serviceId]?.[action.criteriaName || 'default'];
                        return [];
                    })
                );
            })
        )
    );

    getGstFilingInstance$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetFilingInstances),
            debounceTime(500),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(currentFilingYear$),
                this.store$.select(currentFilingMonth$),
                this.store$.select(currentFilingQuarter$),
                this.store$.select(getInstancesOrgServicesMap$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getSearchCriteriaMap$),
                this.store$.select(getAppConfiguratorsMap$),
                this.store$.select(getCustomPeriodicSearchMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(
                ([
                    action,
                    organizationId,
                    assetsMetaDataMap,
                    year,
                    month,
                    quarter,
                    servicesMap,
                    appsMap,
                    searchCriteriaMap,
                    getAppConfiguratorsMap,
                    customPeriodicSearchMap,
                    version,
                ]) => {
                    const app = appsMap?.[action?.serviceId];
                    const metaData = assetsMetaDataMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId];
                    const userCriteria = CommonUtilsService.cloneObject(searchCriteriaMap?.[action.serviceId]?.instances?.['filing']);
                    const customPeriodicSearch =
                        customPeriodicSearchMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId];
                    if (!metaData) {
                        !action.isBackgroundProcess &&
                            setTimeout(() => {
                                this.store$.dispatch(actions.GetFilingInstances(action));
                            }, 2000);
                        return [];
                    }
                    const timelineFilter =
                        getAppConfiguratorsMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId]?.wrapperUI
                            ?.timelineFilter;
                    const closureConfiguration = UtilsService.getClosureConfigurationFromUserPreferences(app?.restApiName);
                    const yearFromConfig = closureConfiguration['selection.standard.year'];
                    const monthFromConfig = closureConfiguration['selection.standard.month'];
                    const quarterFromConfig = closureConfiguration['selection.standard.quarter'];
                    const yearValue = UtilsService.getFilingYearField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    )
                        ? year || yearFromConfig
                        : undefined;
                    const monthValue = UtilsService.getFilingMonthField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    )
                        ? month || monthFromConfig
                        : undefined;
                    const quarterField = UtilsService.getFilingQuarterField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    );
                    const quarterValue: any = quarterField ? quarter || quarterFromConfig : quarterFromConfig || undefined;
                    const criteriaName = UtilsService.getTimelineCriteriaName(yearValue, monthValue, quarterValue, closureConfiguration);
                    const searchCriteria =
                        userCriteria ||
                        UtilsService.getGSTRFilingSearchCriteria(
                            timelineFilter,
                            metaData,
                            action.serviceId,
                            yearValue,
                            monthValue,
                            quarterValue,
                            undefined,
                            undefined,
                            undefined,
                            customPeriodicSearch,
                            closureConfiguration
                        );
                    searchCriteria['noAlerts'] = action.noAlerts;
                    const loading = servicesMap?.[action.serviceId]?.loading?.instances;
                    if (loading) {
                        return [];
                    }
                    !action.isBackgroundProcess &&
                        this.store$.dispatch(
                            actions.SetInstancesLoading({
                                category: 'instances',
                                loading: true,
                                organizationId,
                                serviceId: action.serviceId,
                            })
                        );
                    return from(this._api.instances.searchInstances(searchCriteria as any)).pipe(
                        map(res => {
                            !action.isBackgroundProcess &&
                                this.store$.dispatch(
                                    actions.SetInstancesLoading({
                                        category: 'instances',
                                        loading: false,
                                        organizationId,
                                        serviceId: action.serviceId,
                                    })
                                );
                            return actions.SetSearchInstancesResponse({
                                instances: res.instances,
                                serviceId: action.serviceId,
                                criteriaName,
                                organizationId,
                            });
                        }),
                        catchError(() =>
                            !action.isBackgroundProcess
                                ? of(
                                      actions.SetInstancesLoading({
                                          category: 'instances',
                                          loading: false,
                                          organizationId,
                                          serviceId: action.serviceId,
                                      })
                                  )
                                : []
                        )
                    );
                }
            )
        )
    );

    getOTPInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetOTPInstance),
            delay(500),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getOTPAuthApp$),
                this.store$.select(getSubscribedApps$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getInstancesOrgServicesMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, organizationId, otpApp, apps, assetsMetaDataMap, servicesMap, version]) => {
                if (!otpApp?.serviceId && apps.length > 0) {
                    return [];
                }
                const metaData = assetsMetaDataMap?.[otpApp?.serviceId]?.[otpApp?.assetMetaUId || version?.[otpApp?.serviceId]?.metadataId];
                if (!metaData) {
                    this.store$.dispatch(actions.GetOTPInstance(action));
                    return [];
                }
                const criteriaName = action.gstinNumber;
                const searchCriteria = UtilsService.getGSTROTPSearchCriteria(metaData, action.gstinNumber);
                const loading = servicesMap?.[otpApp.serviceId]?.loading?.statuses;
                if (loading) {
                    return [];
                }
                this.store$.dispatch(
                    actions.SetInstancesLoading({
                        category: 'instances',
                        loading: true,
                        organizationId,
                        serviceId: otpApp.serviceId,
                    })
                );
                return from(
                    this._api.instances.searchInstances({
                        ...searchCriteria,
                        noAlerts: true,
                    } as any)
                ).pipe(
                    map(res => {
                        this.store$.dispatch(
                            actions.SetInstancesLoading({
                                category: 'instances',
                                loading: false,
                                organizationId,
                                serviceId: otpApp.serviceId,
                            })
                        );
                        return actions.SetSearchInstancesResponse({
                            instances: res.instances,
                            serviceId: otpApp.serviceId,
                            criteriaName,
                            organizationId,
                        });
                    }),
                    catchError(() =>
                        of(
                            actions.SetInstancesLoading({
                                category: 'instances',
                                loading: false,
                                organizationId,
                                serviceId: otpApp.serviceId,
                            })
                        )
                    )
                );
            })
        )
    );

    getInstanceEntityErrors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstanceState),
            delay(500),
            withLatestFrom(this.store$.select(getSubscribedAppsMap$), this.store$.select(getInstancesEntityStatus$)),
            mergeMap(([action, services, instanceErrors]) => {
                const service = services[action.serviceId];
                const errors = instanceErrors?.[action.instanceId];
                if (errors !== undefined) {
                    return [];
                }
                return from(
                    this._api.instances.checkInstanceStatus({
                        instanceId: action.instanceId,
                        restApiName: service.restApiName,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map((res: { [property: string]: string }) =>
                        actions.SetInstanceEntityErrors({
                            instanceId: action.instanceId,
                            state: res,
                        })
                    ),
                    catchError(() => [])
                );
            })
        )
    );

    getInstanceErrors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstanceState),
            delay(500),
            withLatestFrom(this.store$.select(getSubscribedAppsMap$), this.store$.select(getInstancesState$)),
            mergeMap(([action, services, instancesErrors]) => {
                const service = services[action.serviceId];
                const errors = instancesErrors?.[action.instanceId];
                if (errors !== undefined) {
                    return [];
                }
                return from(
                    this._api.instances.getInstanceState({
                        instanceId: action.instanceId,
                        restApiName: service.restApiName,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(res =>
                        actions.SetInstanceState({
                            instanceId: action.instanceId,
                            state: res?.instanceInfo,
                        })
                    ),
                    catchError(() => [])
                );
            })
        )
    );

    cancelInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CancelInstances),
            delay(500),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(currentFilingYear$),
                this.store$.select(currentFilingMonth$),
                this.store$.select(currentFilingQuarter$)
            ),
            mergeMap(([action, services, organizationId, year, month, quarter]) => {
                const service = services[action.serviceId];
                const searchCriteriaString = action.searchCriteriaString || `${year || ''}|${month || ''}|${quarter || ''}`;
                return from(
                    this._api.instances.cancelInstancesViaPromise({
                        instanceId: action.instanceIds,
                        restApiName: service.restApiName,
                        comment: action.comment,
                    })
                ).pipe(
                    map(() =>
                        actions.RemoveInstances({
                            instanceIds: action.instanceIds,
                            serviceId: action.serviceId,
                            organizationId,
                            criteria: searchCriteriaString,
                        })
                    ),
                    catchError(() => [])
                );
            })
        )
    );

    getProcessStatuses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetProcessStatuses),
            delay(500),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getInstancesOrgServicesMap$)),
            mergeMap(([action, organizationId, servicesMap]) => {
                const loading = servicesMap?.[action.serviceId]?.loading?.statuses;
                if (loading) {
                    return [];
                }
                this.store$.dispatch(
                    actions.SetInstancesLoading({
                        category: 'statuses',
                        loading: true,
                        organizationId,
                        serviceId: action.serviceId,
                    })
                );
                return from(
                    this._api.requests.getStatusBasedProcesses({
                        serviceId: action.serviceId,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map((res: any) => {
                        this.store$.dispatch(
                            actions.SetInstancesLoading({
                                category: 'statuses',
                                loading: false,
                                organizationId,
                                serviceId: action.serviceId,
                            })
                        );
                        return actions.SetProcessStatuses({
                            organizationId: organizationId,
                            serviceId: action.serviceId,
                            statuses: res.response.statuses,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(
                            actions.SetInstancesLoading({
                                category: 'statuses',
                                loading: false,
                                organizationId,
                                serviceId: action.serviceId,
                            })
                        );
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    selectProcessStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.SelectProcessStatus),
            delay(500),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizationId]) =>
                of(
                    actions.SetSelectedProcessStatus({
                        organizationId: organizationId,
                        serviceId: action.serviceId,
                        processStatus: action.processStatus,
                    })
                )
            )
        )
    );

    getInstanceByProcessStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstancesByProcessStatus),
            delay(500),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(currentFilingYear$),
                this.store$.select(currentFilingMonth$),
                this.store$.select(currentFilingQuarter$),
                this.store$.select(getInstancesOrgServicesMap$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppConfiguratorsMap$),
                this.store$.select(getCustomPeriodicSearchMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(
                ([
                    action,
                    organizationId,
                    assetsMetaDataMap,
                    year,
                    month,
                    quarter,
                    servicesMap,
                    appsMap,
                    appConfiguratorsMap,
                    customPeriodicSearchMap,
                    version,
                ]) => {
                    const app = appsMap?.[action?.serviceId];
                    const metaData = assetsMetaDataMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId];
                    if (!metaData) {
                        this.store$.dispatch(actions.GetFilingInstances(action));
                        return [];
                    }
                    const customPeriodicSearch =
                        customPeriodicSearchMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId];
                    const timelineFilter =
                        appConfiguratorsMap?.[action.serviceId]?.[app?.assetMetaUId || version?.[app?.serviceId]?.metadataId]?.wrapperUI
                            ?.timelineFilter;
                    const closureConfiguration = UtilsService.getClosureConfigurationFromUserPreferences(app?.restApiName);
                    const yearFromConfig = closureConfiguration['selection.standard.year'];
                    const monthFromConfig = closureConfiguration['selection.standard.month'];
                    const quarterFromConfig = closureConfiguration['selection.standard.quarter'];
                    const yearValue = UtilsService.getFilingYearField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    )
                        ? year || yearFromConfig
                        : undefined;
                    const monthValue = UtilsService.getFilingMonthField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    )
                        ? month || monthFromConfig
                        : undefined;
                    const quarterField = UtilsService.getFilingQuarterField(
                        customPeriodicSearch,
                        timelineFilter,
                        metaData?.getPrimaryEntity(),
                        closureConfiguration
                    );
                    const quarterValue: any = quarterField ? quarter || quarterFromConfig : quarterFromConfig || undefined;
                    const searchCriteriaString = UtilsService.getTimelineCriteriaName(yearValue, monthValue, quarterValue, closureConfiguration);
                    const searchCriteria = UtilsService.getGSTRFilingSearchCriteria(
                        timelineFilter,
                        metaData,
                        action.serviceId,
                        yearValue,
                        monthValue,
                        quarterValue,
                        undefined,
                        action.processStatus,
                        undefined,
                        customPeriodicSearch,
                        closureConfiguration
                    );
                    searchCriteria['noAlerts'] = action.noAlerts;
                    const loading = servicesMap?.[action.serviceId]?.loading?.statuses;
                    if (loading) {
                        return [];
                    }
                    this.store$.dispatch(
                        actions.SetInstancesLoading({
                            category: 'instances',
                            loading: true,
                            organizationId,
                            serviceId: action.serviceId,
                        })
                    );
                    return from(this._api.instances.searchInstances(searchCriteria as any)).pipe(
                        map(res => {
                            this.store$.dispatch(
                                actions.SetInstancesLoading({
                                    category: 'instances',
                                    loading: false,
                                    organizationId,
                                    serviceId: action.serviceId,
                                })
                            );
                            return actions.SetInstancesByProcessStatus({
                                organizationId,
                                serviceId: action.serviceId,
                                criteria: searchCriteriaString,
                                processStatus: action.processStatus,
                                instances: res.instances.reduce((instanceIds, instance) => {
                                    instanceIds.push(instance.assetDataId);
                                    return instanceIds;
                                }, []),
                            });
                        }),
                        catchError(() =>
                            of(
                                actions.SetInstancesLoading({
                                    category: 'instances',
                                    loading: false,
                                    organizationId,
                                    serviceId: action.serviceId,
                                })
                            )
                        )
                    );
                }
            )
        )
    );

    CreateBulkReportsDownloadRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CreateBulkReportsDownloadRequest),
            delay(500),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(currentFilingYear$),
                this.store$.select(currentFilingMonth$),
                this.store$.select(currentFilingQuarter$),
                this.store$.select(getInstancesOrgServicesMap$)
            ),
            mergeMap(([action, subscribedApps, year, month, quarter, instancesMap]) => {
                const app = subscribedApps?.[action.serviceId];
                const selectedInstanceIds =
                    action.instanceIds ||
                    instancesMap?.[action.serviceId]?.selectedInstances?.[`${year || ''}|${month || ''}|${quarter || ''}`];
                const payload = {
                    transformations: action.transformationNames,
                    instanceIds: selectedInstanceIds,
                    searchCriteria: action.criteria,
                };
                return from(
                    this._api.instances.createDownloadRequest({
                        restApiName: app.restApiName,
                        payload: payload,
                    })
                ).pipe(
                    map((res: any) =>
                        actions.SetBulkReportsDownloadRequest({
                            downloadUrl: res?.downloadRequest?.response?.url || res?.response?.url,
                            requestId: res?.downloadRequest?.response?.requestId || res?.response?.requestId,
                            serviceId: action.serviceId,
                        })
                    ),
                    catchError(e => {
                        this.store$.dispatch(LoadingBulkReportsError());
                        return of(AlertError({ message: e?.msg }));
                    })
                );
            })
        )
    );

    clearSearchInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.ClearSearchedInstances),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([{ serviceId, searchString }, organizationId]) => {
                this.store$.dispatch(
                    actions.ClearInstancesPagination({
                        serviceId,
                        organizationId,
                    })
                );
                return of(
                    actions.ClearSearchInstances({
                        serviceId: serviceId,
                        criteriaName: searchString,
                        organizationId,
                    })
                );
            })
        )
    );

    getInstances$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstances),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getInstancesOrgServicesMap$),
                this.store$.select(getSelectedAppServiceId$),
                this.store$.select(getCustomInstances$)
            ),
            mergeMap(([action, organizationId, restApiName, serviceMap, serviceId, instances]) => {
                if (instances?.length > 0) {
                    return of(
                        actions.SetInstances({
                            serviceId,
                            organizationId,
                            instances: CommonUtilsService.cloneObject(instances),
                        })
                    );
                }
                const pagination = serviceMap?.[serviceId]?.pagination;
                const data = {
                    fetchSize: pagination?.size || 100,
                    previousPagingState: pagination?.pagingState,
                    noAlerts: action.noAlerts,
                    loadAssetRequests: true,
                    restApiName,
                };
                return this._api.instances.getInstancesStore(data).pipe(
                    map((res: any) => {
                        this.store$.dispatch(
                            actions.SetInstancesPagingState({
                                organizationId,
                                serviceId,
                                pagingState: res?.response?.instances?.nextPagingState
                                    ? encodeURIComponent(res?.response?.instances?.nextPagingState)
                                    : undefined,
                            })
                        );
                        return actions.SetInstances({
                            serviceId: serviceId,
                            organizationId: organizationId,
                            instances: res?.response?.instances?.assetInstances || [],
                        });
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res?.msg || 'Failed to get instances' }));
                    })
                );
            })
        )
    );

    cancelInstance$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CancelInstance),
            mergeMap(action => {
                return from(
                    this._api.instances.cancelInstancesViaPromise({
                        instanceId: action.instanceId,
                        restApiName: action.restAPIName,
                        comment: action.comment,
                    })
                ).pipe(
                    map(() => {
                        !action.noAlerts && this._utils.alertSuccess(translate('Process cancelled successfully'));
                        return actions.UpdateCancelInstance({ instanceId: action.instanceId });
                    }),
                    catchError(err => {
                        !action.noAlerts && this._utils.alertError(err?.msg || translate('Failed to cancel process'));
                        return [];
                    })
                );
            })
        )
    );

    getPreCapturedAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstancePreCapturedAttributes),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getCustomAppsRestApiName$),
                this.store$.select(getPreCapturedAttributes$)
            ),
            mergeMap(([action, organizationId, restApiName, preCapturedAttributes]) => {
                if (preCapturedAttributes?.[action.instanceId]) {
                    return [];
                }
                return this._api.instances
                    .getPreCapturedAttributes({
                        restApiName,
                        requestId: action.instanceId,
                        noAlerts: action.noAlerts,
                    })
                    .pipe(
                        map((res: any) => {
                            return actions.SetInstancePreCapturedAttributes({
                                organizationId,
                                instanceId: action.instanceId,
                                attributes: res?.response?.attributes || [],
                            });
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res?.msg || 'Failed to get pre captured attributes' }));
                        })
                    );
            })
        )
    );

    getInstanceIntegrationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInstanceIntegrationStatus),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizationId]) => {
                return this._api.instances
                    .getIntegrationStatusForInstanceStore({
                        restApiName: action.restAPIName,
                        instanceId: action.instanceId,
                        orgId: organizationId,
                        noAlerts: action.noAlerts,
                    })
                    .pipe(
                        map((res: any) => {
                            return actions.SetInstanceIntegrationStatus({
                                instanceId: action.instanceId,
                                integrationStatus: res?.response?.status,
                            });
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res?.msg || 'Failed to get instance integration status' }));
                        })
                    );
            })
        )
    );

    retryInstanceIntegrationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.RetryInstanceIntegrationStatus),
            mergeMap(_action => {
                return this._api.instances
                    .getIntegrationRefreshStatusStore({
                        requestId: _action.requestId,
                        unitId: _action.unitId,
                        assetId: _action.assetId,
                        bridgeId: _action.bridgeId,
                        serviceId: _action.serviceId,
                        assetDataId: _action.assetDataId,
                        userId: _action.userId,
                        requestLevel: _action.requestLevel,
                        noAlerts: _action.noAlerts,
                    })
                    .pipe(
                        map((res: any) => {
                            return actions.SetInstanceIntegrationStatus({
                                instanceId: _action.assetDataId,
                                integrationStatus: res?.response?.status,
                            });
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res?.msg || 'Failed to get instance integration status' }));
                        })
                    );
            })
        )
    );

    getAllMapTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetAllMapTransformations),
            withLatestFrom(this.store$.select(getInstanceTransformations$)),
            mergeMap(([action, instanceTransformations]) => {
                if (!this._utils.isEmpty(instanceTransformations?.[action?.instanceId])) {
                    return [];
                }
                return this._api.assets.getAllMapTransformations({ assetMetaUId: action.assetUid, noAlerts: action.noAlerts }).pipe(
                    map((res: any) => {
                        return actions.SetAllMapTransformations({
                            instanceId: action.instanceId,
                            transformations: res || {},
                        });
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res?.msg || 'Failed to get all map transformations' }));
                    })
                );
            })
        )
    );

    getDownloadableFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetDownloadableFiles),
            withLatestFrom(this.store$.select(getDownloadableFiles$)),
            mergeMap(([action, downloadableFiles]) => {
                if (downloadableFiles?.[action?.instanceId]?.length > 0) {
                    return [];
                }
                return from(
                    this._api.requests.downloadNewUploadedFiles({
                        resourceId: action?.instanceId,
                        restApiName: action?.restAPIName,
                        noAlerts: action?.noAlerts,
                    })
                ).pipe(
                    map(res => {
                        return actions.SetDownloadableFiles({
                            instanceId: action?.instanceId,
                            files: res || [],
                        });
                    }),
                    catchError(error => {
                        return of(AlertError({ message: error?.msg || 'Failed to get downloadable files' }));
                    })
                );
            })
        )
    );

    getInstanceCollaborators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetCollaborators),
            withLatestFrom(this.store$.select(getCollaborators$)),
            mergeMap(([action, collaborators]) => {
                if (collaborators?.[action?.instanceId]?.length > 0) {
                    return [];
                }
                return from(
                    this._api.collaboration.getCollaboratorsList({
                        assetId: action.assetId,
                        instanceId: action.instanceId,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map((res: any) => {
                        return actions.SetCollaborators({
                            instanceId: action.instanceId,
                            collaborators: res || [],
                        });
                    }),
                    catchError(error => {
                        return of(AlertError({ message: error?.msg || 'Failed to get instance collaborators' }));
                    })
                );
            })
        )
    );
}
